import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import DataTable from "react-data-table-component";
import {
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineSync,
    AiOutlineCheckCircle,
    AiOutlineClose,
    AiOutlineSearch,
} from "react-icons/ai";
import authService from "../../services/authService";

// ===== REAL API SECTION =====
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin`;

async function apiFetchUsers() {
    console.log('Making API request to:', `${API_BASE_URL}/users/`);
    
    const response = await authService.adminApiCall(`${API_BASE_URL}/users/`);
    
    console.log('API Response status:', response.status);
    
    if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API Success response:', data);
    return data.data || [];
}

async function apiEditUser(user) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/users/${user.id}/`, {
        method: 'PUT',
        body: JSON.stringify({
            first_name: user.name,
            last_name: user.lastname,
            email: user.email,
            phone: user.mobile,
            assigned_employee_id: user.assigned_employee_id || null
        })
    });
    
    if (!response.ok) {
        throw new Error('Failed to update user');
    }
    
    return true;
}

async function apiDeleteUser(id) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/users/${id}/delete/`, {
        method: 'DELETE'
    });
    
    if (!response.ok) {
        throw new Error('Failed to delete user');
    }
    
    return true;
}

async function apiChangeStatus(id) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/users/${id}/toggle-status/`, {
        method: 'POST'
    });
    
    if (!response.ok) {
        throw new Error('Failed to change user status');
    }
    
    return true;
}

async function apiFetchEmployees() {
    const response = await authService.adminApiCall(`${API_BASE_URL}/employees/`);
    
    if (!response.ok) {
        throw new Error('Failed to fetch employees');
    }
    
    const data = await response.json();
    return data.data || [];
}

async function apiAssignEmployee(userId, employeeId) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/users/${userId}/assign-employee/`, {
        method: 'POST',
        body: JSON.stringify({
            employee_id: employeeId || null
        })
    });
    
    if (!response.ok) {
        throw new Error('Failed to assign employee');
    }
    
    return true;
}
// ===========================================

export default function Users() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);

    // Pagination / Controls
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Search query
    const [search, setSearch] = useState("");

    // Edit modal states
    const [editRow, setEditRow] = useState(null);
    const [editVals, setEditVals] = useState({ name: "", lastname: "", email: "", mobile: "", assigned_employee_id: null });
    const [editErrs, setEditErrs] = useState({});
    const [editTouched, setEditTouched] = useState({});

    // Delete modal
    const [deleteRow, setDeleteRow] = useState(null);

    // Success message modal
    const [successMsg, setSuccessMsg] = useState("");

    // Employee assignment dropdown state
    const [openDropdown, setOpenDropdown] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const dropdownButtonRefs = useRef({});

    // Update dropdown position on scroll/resize
    useEffect(() => {
        if (!openDropdown) return;

        const updatePosition = () => {
            const button = dropdownButtonRefs.current[openDropdown];
            if (button) {
                const rect = button.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.bottom + window.scrollY + 4,
                    left: rect.left + window.scrollX,
                    width: Math.max(rect.width, 180)
                });
            }
        };

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [openDropdown]);

    // Fetch users and employees on mount
    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                // Check if admin is authenticated before making API call
                const token = localStorage.getItem('adminAuthToken');
                if (!token) {
                    console.error('No admin token found');
                    setData([]);
                    setEmployees([]);
                    setLoading(false);
                    return;
                }
                
                console.log('Fetching users and employees with token:', token.substring(0, 20) + '...');
                const [usersList, employeesList] = await Promise.all([
                    apiFetchUsers(),
                    apiFetchEmployees()
                ]);
                setData(usersList);
                setEmployees(employeesList);
                console.log('Users fetched successfully:', usersList.length);
                console.log('Employees fetched successfully:', employeesList.length);
            } catch (error) {
                console.error('Error fetching data:', error);
                setData([]);
                setEmployees([]);
            }
            setLoading(false);
        }
        fetchData();
    }, []);

    // Filter data by name or email
    const filteredData = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.trim().toLowerCase();
        const filtered = data.filter(
            d => {
                const firstName = (d.first_name || '').toLowerCase();
                const lastName = (d.last_name || '').toLowerCase();
                const email = (d.email || '').toLowerCase();
                const fullName = `${firstName} ${lastName}`.trim();
                
                return firstName.includes(q) ||
                       lastName.includes(q) ||
                       email.includes(q) ||
                       fullName.includes(q);
            }
        );
        // console.log('Search query:', q, 'Filtered results:', filtered.length, 'Total data:', data.length);
        return filtered;
    }, [data, search]);

    // Filter out resigned employees for dropdowns (only show active/working employees)
    const activeEmployees = useMemo(() => {
        return employees.filter(emp => emp.status !== 'resigned');
    }, [employees]);

    // Columns configuration
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "80px",
            minWidth: "56px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
            sortable: false,
            center: true,
        },
        {
            name: "Name",
            selector: row => row.first_name,
            sortable: true,
            minWidth: "110px",
            maxWidth: "230px",
            style: { fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" },
            cell: row => <span title={row.first_name}>{row.first_name || 'N/A'}</span>,
        },
        {
            name: "Last Name",
            selector: row => row.last_name,
            sortable: true,
            minWidth: "110px",
            maxWidth: "230px",
            style: { fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" },
            cell: row => <span title={row.last_name}>{row.last_name || 'N/A'}</span>,
        },
        {
            name: "Email",
            selector: row => row.email,
            sortable: true,
            minWidth: "240px",
            maxWidth: "350px",
            //center:true,
            style: { fontWeight: 500, color: "#116", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            cell: row => <span title={row.email} style={{ display: "block", textAlign: "left" }}>{row.email}</span>,
        },
        {
            name: "Mobile",
            selector: row => row.phone,
            sortable: true,
            minWidth: "130px",
            maxWidth: "200px",
            style: { fontWeight: 500, color: "#164d26", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
            cell: row => <span title={row.phone}>{row.phone || 'N/A'}</span>,
            center: true,
        },
        {
            name: "Status",
            selector: row => row.is_active,
            minWidth: "95px",
            maxWidth: "200px",
            style: { textAlign: "center", whiteSpace: "nowrap" },
            cell: row => (
                <span style={{
                    borderRadius: "14px",
                    padding: "5px 15px",
                    fontWeight: 700,
                    color: "#fff",
                    fontSize: 14,
                    background: row.is_active ? "#21bb55" : "#dc2626",
                    boxShadow: row.is_active ? "0 2px 8px #21bb551a" : "0 2px 9px #dc262611",
                    letterSpacing: ".02em",
                    userSelect: "none",
                    display: "inline-block",
                    minWidth: 70
                }}>
                    {row.is_active ? "Active" : "Inactive"}
                </span>
            ),
            center: true,
            sortable: false,
        },
        {
            name: "Assigned To",
            minWidth: "180px",
            maxWidth: "250px",
            style: { textAlign: "center", whiteSpace: "nowrap" },
            cell: row => {
                const isOpen = openDropdown === row.id;
                const assignedEmployee = employees.find(emp => emp.id === row.assigned_employee_id);
                
                // If employee is assigned, show only the name (no dropdown)
                if (assignedEmployee) {
                    return (
                        <span
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                background: "#e8f4fd",
                                color: "#215ea7",
                                fontWeight: 600,
                                fontSize: 13,
                                display: "inline-block",
                                minWidth: 140,
                            }}
                        >
                            {assignedEmployee.name}
                        </span>
                    );
                }
                
                // If no employee assigned, show dropdown button
                return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <button
                            ref={(el) => {
                                if (el) {
                                    dropdownButtonRefs.current[row.id] = el;
                                } else {
                                    delete dropdownButtonRefs.current[row.id];
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isOpen) {
                                    setOpenDropdown(null);
                                } else {
                                    const button = dropdownButtonRefs.current[row.id];
                                    if (button) {
                                        const rect = button.getBoundingClientRect();
                                        setDropdownPosition({
                                            top: rect.bottom + window.scrollY + 4,
                                            left: rect.left + window.scrollX,
                                            width: Math.max(rect.width, 180)
                                        });
                                    }
                                    setOpenDropdown(row.id);
                                }
                            }}
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "1.5px solid #aab6fe",
                                background: "#f0f7ff",
                                color: "#666",
                                fontWeight: 600,
                                fontSize: 13,
                                cursor: "pointer",
                                minWidth: 140,
                                textAlign: "left",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8
                            }}
                        >
                            <span>Assign Employee</span>
                            <span style={{ fontSize: 10 }}>{isOpen ? "▲" : "▼"}</span>
                        </button>
                    </div>
                );
            },
            center: true,
            sortable: false,
            ignoreRowClick: true,
        },
        {
            name: "Actions",
            minWidth: "105px",
            maxWidth: "130px",
            style: { whiteSpace: "nowrap" },
            cell: row => (
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                    whiteSpace: "nowrap",
                }}>
                    <button
                        onClick={() => openEdit(row)}
                        style={iconBtn('#3193eb')}
                        aria-label="Edit"
                        title="Edit"
                    >
                        <AiOutlineEdit size={18} />
                    </button>
                    <button
                        onClick={() => openDelete(row)}
                        style={iconBtn('#dc2626')}
                        aria-label="Delete"
                        title="Delete"
                    >
                        <AiOutlineDelete size={18} />
                    </button>
                    <button
                        onClick={() => handleChangeStatus(row)}
                        style={iconBtn('#2979ff')}
                        aria-label="Change Status"
                        title="Change Status"
                    >
                        <AiOutlineSync size={18} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            center: true,
            sortable: false,
        }
    ], [currentPage, perPage, openDropdown, employees]);

    // Pagination handlers
    function handlePageChange(page) { setCurrentPage(page); }
    function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); setCurrentPage(page); }

    // Edit modal open
    function openEdit(row) {
        setEditRow(row);
        setEditVals({
            name: row.first_name || '',
            lastname: row.last_name || '',
            email: row.email || '',
            mobile: row.phone || '',
            assigned_employee_id: row.assigned_employee_id || null
        });
        setEditErrs({});
        setEditTouched({});
    }

    // Validate edit fields
    function validate(vals) {
        const errs = {};
        if (!vals.name.trim()) errs.name = "First name required";
        else if (!/^[A-Za-z]+$/.test(vals.name.trim())) errs.name = "Alphabets only";
        if (!vals.lastname.trim()) errs.lastname = "Last name required";
        else if (!/^[A-Za-z]+$/.test(vals.lastname.trim())) errs.lastname = "Alphabets only";
        if (!vals.email.trim()) errs.email = "Email required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email.trim())) errs.email = "Invalid email";
        if (!vals.mobile.trim()) errs.mobile = "Mobile required";
        else if (!/^[0-9 ]+$/.test(vals.mobile.trim())) errs.mobile = "Numbers and spaces only";
        return errs;
    }

    function isEditFormValid() {
        return editVals.name?.trim() && 
               editVals.lastname?.trim() && 
               editVals.email?.trim() && 
               editVals.mobile?.trim() &&
               Object.keys(validate(editVals)).length === 0;
    }

    function handleInputChange(e) {
        const { name, value } = e.target;
        setEditVals(v => ({ ...v, [name]: value }));
        setEditTouched(t => ({ ...t, [name]: true }));
        setEditErrs(validate({ ...editVals, [name]: value }));
    }

    async function handleSave(e) {
        e.preventDefault();
        const errs = validate(editVals);
        setEditErrs(errs);
        setEditTouched({ name: true, lastname: true, email: true, mobile: true });
        if (Object.keys(errs).length) return;

        setLoading(true);
        try {
            await apiEditUser({ 
                id: editRow.id, 
                name: editVals.name,
                lastname: editVals.lastname,
                email: editVals.email,
                mobile: editVals.mobile,
                assigned_employee_id: editVals.assigned_employee_id
            });
            setData(await apiFetchUsers());
            setSuccessMsg("Saved successfully!");
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);

        setEditRow(null);
        setEditVals({ name: "", lastname: "", email: "", mobile: "", assigned_employee_id: null });
    }

    // Delete modal open
    function openDelete(row) { setDeleteRow(row); }

    async function handleDeleteYes() {
        setLoading(true);
        try {
            await apiDeleteUser(deleteRow.id);
            setData(await apiFetchUsers());
            setSuccessMsg("Deleted successfully!");
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);

        setDeleteRow(null);
    }

    // Status toggle
    async function handleChangeStatus(row) {
        setLoading(true);
        try {
            await apiChangeStatus(row.id);
            setData(await apiFetchUsers());
        } catch (error) {
            setSuccessMsg("Error: " + error.message);
        }
        setLoading(false);
    }

    function closeSuccess() { setSuccessMsg(""); }

    // Custom styles with nowrap & adjusted widths
    const customStyles = {
        table: { style: { width: "100%" } },
        rows: { style: { fontSize: 15, background: "#f8fafc", whiteSpace: "nowrap" } },
        headRow: { style: { background: "#eceffd", color: "#433ca", fontWeight: 700, fontSize: 15 } },
        headCells: { style: { borderBottom: "1.6px solid #a5a6ff", padding: "6px 9px" } },
        cells: { style: { padding: "6px 9px" } },
        pagination: { style: { borderTop: "1px solid #aab6fe", color: "#433ca", fontWeight: 600 } }
    };

    // Search input ref to maintain focus
    const searchInputRef = useRef(null);

    // Focus management for search input
    const handleSearchFocus = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Search Input Bar Component - memoized to prevent re-creation
    const SearchBar = useCallback(() => {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div 
                    style={{
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 6,
                        border: "1.5px solid #aab6fe",
                        background: "#f0f7ff",
                        padding: "0 10px",
                        maxWidth: 280,
                        width: "100%"
                    }}
                    onClick={handleSearchFocus}
                >
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search name or email"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            border: "none",
                            flexGrow: 1,
                            height: 30,
                            fontSize: 14,
                            outline: "none",
                            background: "transparent"
                        }}
                        aria-label="Search users"
                        autoComplete="off"
                    />
                    {search.trim() ? (
                        <button
                            type="button"
                            onClick={() => {
                                setSearch("");
                                // Focus back to input after clearing
                                setTimeout(handleSearchFocus, 0);
                            }}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                display: "flex",
                                alignItems: "center"
                            }}
                            title="Clear search"
                        >
                            <AiOutlineClose size={16} style={{ color: "#666" }} />
                        </button>
                    ) : (
                        <AiOutlineSearch size={20} style={{ color: "#2979ff" }} />
                    )}
                </div>
                {search.trim() && (
                    <div style={{
                        fontSize: 12,
                        color: "#666",
                        background: "#f0f0f0",
                        padding: "4px 8px",
                        borderRadius: 4,
                        whiteSpace: "nowrap"
                    }}>
                        {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        );
    }, [search, filteredData.length]);

    return (
        <section style={{ //maxWidth: 1260, margin: "auto", 
            padding: 20
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
                <h1 style={{ //fontWeight: 900, 
                    //fontSize: 28, color: "#215ea7", display: "flex", alignItems: "center", gap: 12 
                    // 
                    display: "flex",
                    alignItems: "center", gap: 13,
                    marginBottom: 13,
                    fontSize: 38,
                    marginRight: 10,
                    color: "#215ea7",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)",
                }}>
                    <AiOutlineEdit size={32} color="#215ea7" />
                    User Management
                </h1>
                <SearchBar key="search-bar" />
            </div>

            <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                highlightOnHover
                striped
                responsive
                persistTableHead
                progressPending={loading}
                customStyles={customStyles}
                noHeader
            />

            {/* Employee Assignment Dropdown - Rendered via Portal */}
            {openDropdown && (() => {
                const row = data.find(r => r.id === openDropdown);
                if (!row) return null;
                const assignedEmployee = employees.find(emp => emp.id === row.assigned_employee_id);
                
                return createPortal(
                    <>
                        <div
                            style={{
                                position: "fixed",
                                top: dropdownPosition.top,
                                left: dropdownPosition.left,
                                width: dropdownPosition.width,
                                background: "#fff",
                                border: "1.5px solid #aab6fe",
                                borderRadius: "6px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                zIndex: 10000,
                                maxHeight: 250,
                                overflowY: "auto",
                                minWidth: 180
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {activeEmployees.length === 0 ? (
                                <div style={{ padding: "12px", textAlign: "center", color: "#666", fontSize: 13 }}>
                                    No employees available
                                </div>
                            ) : (
                                activeEmployees.map(emp => (
                                    <div
                                        key={emp.id}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            setLoading(true);
                                            try {
                                                await apiAssignEmployee(row.id, emp.id);
                                                setData(await apiFetchUsers());
                                                setOpenDropdown(null);
                                                setSuccessMsg("Employee assigned successfully!");
                                            } catch (error) {
                                                setSuccessMsg("Error: " + error.message);
                                            }
                                            setLoading(false);
                                        }}
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #f0f0f0",
                                            background: row.assigned_employee_id === emp.id ? "#e8f4fd" : "#fff",
                                            color: row.assigned_employee_id === emp.id ? "#215ea7" : "#333",
                                            fontWeight: row.assigned_employee_id === emp.id ? 600 : 500,
                                            fontSize: 13
                                        }}
                                        onMouseEnter={(e) => {
                                            if (row.assigned_employee_id !== emp.id) {
                                                e.target.style.background = "#f0f7ff";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (row.assigned_employee_id !== emp.id) {
                                                e.target.style.background = "#fff";
                                            }
                                        }}
                                    >
                                        {emp.name}
                                    </div>
                                ))
                            )}
                        </div>
                        <div
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                background: "transparent"
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                            }}
                        />
                    </>,
                    document.body
                );
            })()}

            {/* Edit Dialog */}
            {editRow && (
                <Modal onClose={() => setEditRow(null)} title="Edit User">
                    <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {["name", "lastname", "email", "mobile"].map((field, idx) => (
                            <div key={field}>
                                <label style={labelStyle}>
                                    {["First Name", "Last Name", "Email", "Mobile"][idx]}
                                    <span style={{ color: "#dd362b" }}> *</span>
                                </label>
                                <input
                                    name={field}
                                    type={field === "email" ? "email" : "text"}
                                    value={editVals[field]}
                                    onChange={handleInputChange}
                                    autoFocus={idx === 0}
                                    style={inputStyle}
                                    placeholder={`Enter ${field === "email" ? "email" : field}`}
                                    maxLength={field === "email" ? 60 : 30}
                                    required
                                />
                                {editTouched[field] && editErrs[field] && (
                                    <span style={errStyle}>{editErrs[field]}</span>
                                )}
                            </div>
                        ))}
                        <div>
                            <label style={labelStyle}>
                                Assigned To
                            </label>
                            <select
                                name="assigned_employee_id"
                                value={editVals.assigned_employee_id || ""}
                                onChange={(e) => {
                                    const value = e.target.value ? parseInt(e.target.value) : null;
                                    setEditVals(v => ({ ...v, assigned_employee_id: value }));
                                }}
                                style={{
                                    ...inputStyle,
                                    cursor: "pointer",
                                    appearance: "auto"
                                }}
                            >
                                <option value="">-- Select Employee --</option>
                                {activeEmployees.map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
                            <button type="button" onClick={() => setEditRow(null)} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
                            <button
                                type="submit"
                                style={{
                                    ...modalBtn("#215ea7", "#fff"),
                                    ...(!isEditFormValid() ? {
                                        background: "#ccc",
                                        color: "#666",
                                        cursor: "not-allowed",
                                        opacity: 0.6
                                    } : {})
                                }}
                                disabled={!isEditFormValid()}
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Delete Dialog */}
            {deleteRow && (
                <Modal onClose={() => setDeleteRow(null)} title="Delete User">
                    <div style={{ fontWeight: "600", fontSize: 18, marginBottom: 18 }}>
                        Are you sure you want to delete <b>{deleteRow.name} {deleteRow.lastname}</b>?
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
                        <button onClick={() => setDeleteRow(null)} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
                        <button onClick={handleDeleteYes} style={modalBtn("#dd362b", "#fff")}>Delete</button>
                    </div>
                </Modal>
            )}

            {/* Success Modal */}
            {successMsg && (
                <SuccessModal message={successMsg} onClose={closeSuccess} />
            )}

            {/* Responsive fix styles */}
            <style>
                {`
          @media (max-width: 850px) {
            .rdt_TableCol, .rdt_TableCell {
              padding-left: 6px !important;
              padding-right: 6px !important;
              font-size: 13px;
            }
          }
        `}
            </style>
        </section>
    );
}

// == Helpers & Styles ==

function iconBtn(bg) {
    return {
        background: bg,
        color: "#fff",
        border: "none",
        borderRadius: 7,
        padding: 6,
        margin: 0,
        cursor: "pointer",
        boxShadow: bg === "#2979ff" ? "0 2px 5px #2979ff33" : undefined,
        width: 34,
        height: 34,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        transition: "background-color 0.2s ease"
    };
}

function modalBtn(bg, color, px = 16, py = 8) {
    return {
        padding: `${py}px ${px}px`,
        background: bg,
        color: color,
        border: "none",
        borderRadius: 7,
        fontWeight: 700,
        fontSize: 15,
        cursor: "pointer",
        outline: "none",
    };
}

const labelStyle = { color: "#215ea7", fontWeight: 700, fontSize: 15, marginBottom: 6, display: "block" };

const inputStyle = {
    padding: 10,
    fontSize: 16,
    borderRadius: 7,
    border: "1.6px solid #aec0fa",
    outline: "none",
    width: "100%",
    fontWeight: 600,
    color: "#243567",
    letterSpacing: ".04em",
    background: "#f6f9ff"
};

const errStyle = { color: "#dd362b", fontWeight: 600, fontSize: 13, marginTop: 2 };

function validate(vals) {
    const errs = {};
    if (!vals.name.trim()) errs.name = "First name required";
    else if (!/^[A-Za-z]+$/.test(vals.name.trim())) errs.name = "Alphabets only";
    if (!vals.lastname.trim()) errs.lastname = "Last name required";
    else if (!/^[A-Za-z]+$/.test(vals.lastname.trim())) errs.lastname = "Alphabets only";
    if (!vals.email.trim()) errs.email = "Email required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email.trim())) errs.email = "Invalid email";
    if (!vals.mobile.trim()) errs.mobile = "Mobile required";
    else if (!/^[0-9 ]+$/.test(vals.mobile.trim())) errs.mobile = "Numbers and spaces only";
    return errs;
}

// ====== Components & Styles ======

function Modal({ onClose, title, children }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modalTitle"
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10001
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#f9fcfe",
                    borderRadius: 15,
                    position: "relative",
                    padding: "39px 39px 23px 39px",
                    minWidth: 350,
                    maxWidth: 490,
                    width: "99vw",
                    boxShadow: "0 10px 40px #a5b4fc33"
                }}
            >
                <h3 id="modalTitle" style={{ fontWeight: 800, color: "#215ea7", margin: 0, marginBottom: 18, fontSize: 22, letterSpacing: ".02em" }}>
                    {title}
                </h3>
                {children}
                <button
                    aria-label="Close"
                    onClick={onClose}
                    style={{
                        position: "absolute", top: 14, right: 14, background: "transparent",
                        border: "none", fontSize: 23, cursor: "pointer", color: "#666", userSelect: "none",
                    }}
                >
                    <AiOutlineClose />
                </button>
            </div>
        </div>
    );
}

function SuccessModal({ message, onClose }) {
    return (
        <div
            role="alertdialog"
            aria-modal="true"
            onClick={onClose}
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10002,
                display: "flex", justifyContent: "center", alignItems: "center"
            }}>
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#fafff9",
                    padding: "36px 30px 26px 30px",
                    borderRadius: 16,
                    textAlign: "center",
                    boxShadow: "0 8px 36px #53ec8860",
                    color: "#11602c"
                }}>
                <AiOutlineCheckCircle
                    size={62}
                    color="#17bb5f"
                    style={{ marginBottom: 13, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
                />
                <div style={{ fontWeight: 800, fontSize: 21, marginBottom: 2 }}>{message}</div>
                <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 17, 10)}>Close</button>
                <style>{`
                    @keyframes pop-tick {
                        0% { opacity: 0; transform: scale(0.46);}
                        67% { opacity: 1; transform: scale(1.14);}
                        100% { opacity: 1; transform: scale(1);}
                    }
                `}</style>
            </div>
        </div>
    );
}